import { useCallback, useState } from "react";
import { showToast } from "../../../../common/Toaster/Toaster";
import { uploadBulkFile } from "../BulkOrderService";
import { BULK_UPLOAD_MESSAGES } from "../Json/BulkUploadMessages";
import { validateFile } from "../Utils/fileUtils";

const TOAST_TYPE = { SUCCESS: "success", ERROR: "error" };

/**
 * Owns the select -> validate -> upload -> result cycle for one upload type.
 *
 * @param   {object|null} config  An entry from BulkUploadConfigJSON.
 * @returns {{
 *   file: File|null,
 *   isUploading: boolean,
 *   result: object|null,
 *   error: string|null,
 *   selectFile: (file: File) => boolean,
 *   clearFile: () => void,
 *   upload: () => Promise<void>,
 * }}
 */
const useBulkUpload = (config) => {
  const [file, setFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  const selectFile = useCallback(
    (nextFile) => {
      const validationError = validateFile(nextFile);

      if (validationError) {
        showToast(validationError, TOAST_TYPE.ERROR);
        return false;
      }

      setFile(nextFile);
      reset();
      return true;
    },
    [reset]
  );

  const clearFile = useCallback(() => {
    setFile(null);
    reset();
  }, [reset]);

  const upload = useCallback(async () => {
    if (!config) return;

    const validationError = validateFile(file);

    if (validationError) {
      showToast(validationError, TOAST_TYPE.ERROR);
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const uploadResult = await uploadBulkFile(config.id, file);

      setResult(uploadResult);
      setFile(null);

      showToast(
        uploadResult.file
          ? BULK_UPLOAD_MESSAGES.result.downloadHint
          : uploadResult.message || BULK_UPLOAD_MESSAGES.result.completed,
        TOAST_TYPE.SUCCESS
      );
    } catch (uploadError) {
      setResult(null);
      setError(uploadError.message);
      showToast(uploadError.message, TOAST_TYPE.ERROR);
    } finally {
      setIsUploading(false);
    }
  }, [config, file]);

  return { file, isUploading, result, error, selectFile, clearFile, upload };
};

export default useBulkUpload;
